import { Observable, Subscription } from "rxjs";
import _Vue from "vue";
import {
  isAppClientConfigured,
  listResourceScopes,
  createAppClient,
  createAdminUser,
  createUser,
  needs2FA,
  logout,
  exchangeAuthCode,
  cleanupLocalStorage,
  incrementAndGet,
} from "@/api/auth";
import {
  httpClient,
  _unwrapAxiosResponse,
  registerAuthErrorObserver,
  registerSuccessObserver,
  ZnaRequestError,
} from "@/api/http";
import { Store } from "vuex";
import { GlobalState } from "@/store/global";
import { MonatsabschlussZeile, CalendarDay, Kassenbuch, KassenbuchService, Ausgabe, AbrechnungService } from "@/api/Abrechnung";
import {
  TerminReihe,
  Termin,
  TermineService,
  DienstplanEintrag,
  Dame,
} from "@/api/Termine";
import { StudioService, Studio } from "@/api/Studio";
import { Moment } from "moment";

interface ZnaApi {
  getTitle(): string;
  setTitle(title: string): void;
  registerAuthErrorObserver(
    next?: ((value: ZnaRequestError) => void) | undefined,
    error?: ((error: ZnaRequestError) => void) | undefined,
    complete?: (() => void) | undefined
  ): Subscription;
  registerSuccessObserver(
    next?: ((value: any) => void) | undefined,
    error?: ((error: ZnaRequestError) => void) | undefined,
    complete?: (() => void) | undefined
  ): Subscription;
  isAuthenticated(): boolean;
  isStudio(): boolean;
  darfTerminplanVonStudio(studioBezeichner: string): boolean;
  darfDienstplanVonStudioLesen(studioBezeichner: string): boolean;
  darfDienstplanVonStudioSchreiben(studioBezeichner: string): boolean;
  darfBuchhaltung(): boolean;
  darfBuchhaltungTagesabschluss(): boolean;
  darfNotAus(): boolean;
  darfTageAuswaehlen(): boolean;
  darfGeloeschteTermine(): boolean;
  darfTageLoeschen(): boolean;
  isAppClientConfigured(): boolean;
  listResourceScopes(): Observable<any[]>;
  createAppClient(name: string, scope: string[]): Observable<any>;
  createAdminUser(username: string, otpSecret: string): Observable<any>;
  createUser(username: string): Observable<any>;
  needs2FA(username: string): Observable<boolean>;
  logout(forceLogin: boolean): void;
  exchangeAuthCode(code: string): Observable<any>;
  cleanupLocalStorage(): void;
  increment(sequenzName: string): Observable<number>;
  getStudios(): Observable<Studio[]>;
  getStudio(studioBezeichner: string): Observable<Studio>;
  studioSpeichern(studio: Studio): Observable<Studio>;
  getDienstplanKw(studio: Studio, tag: Moment): Observable<DienstplanEintrag[]>;
  getDienstplanTag(
    studio: Studio,
    tag: Moment,
    gesamtenTagAnzeigen: boolean
  ): Observable<DienstplanEintrag[]>;
  dienstSpeichern(dienst: DienstplanEintrag): Observable<DienstplanEintrag>;
  dienstLoeschen(dienst: DienstplanEintrag): Observable<any>;
  getVerfuegbareDamen(studio: Studio): Observable<Dame[]>;
  getTermine(
    studio: Studio,
    datum: Moment,
    gesamtenTagAnzeigen: boolean
  ): Observable<TerminReihe[]>;
  getTermineMonat(
    studio: Studio,
    monat: Moment,
    alleDamenZurueckgeben: boolean,
    dame?: Dame
  ): Observable<MonatsabschlussZeile[]>;
  buchungstagVerschieben(
    studio: Studio,
    monat: Moment,
    dame: Dame,
    vonTag: string,
    nachTag: string,
    alleDamenZurueckgeben: boolean
  ): Observable<MonatsabschlussZeile[] | string>;
  buchungstagSpeichern(
    studio: Studio,
    monat: Moment,
    dame: Dame,
    tag: CalendarDay
  ): Observable<void>;
  terminSpeichern(standort: Studio, termin: Termin): Observable<Termin>;
  termineAufHeute(studio: Studio): Observable<Termin[]>;
  tagesTermineLoeschen(studio: Studio, tag: Moment): Observable<void>;
  alleBuchungsdatenLoeschen(): Observable<void>;
  monatsabschlussNeuBerechnen(studio: Studio, monat: Moment, alleDamenZurueckgeben: boolean, dame?: Dame): Observable<MonatsabschlussZeile[]>;
  updateMonatsabschlussDame(studio: Studio, tag: Moment, dame: number, miete: number): Observable<string[]>;
  getKassenbuch(monat: Moment): Observable<Kassenbuch>;
  getKassenbuchEinnahmen(monat: Moment): Observable<MonatsabschlussZeile[]>;
  getKassenbuchAusgaben(monat: Moment): Observable<Ausgabe[]>;
  updateKassenbuch(monat: Moment, kassenbuch: Kassenbuch): Observable<string[]>;
  monatseinnahmenVerschieben(monat: Moment, sourceStudio: Studio, targetStudio: Studio, betrag: number): Observable<string[]>;
}

export interface ZnaApiOptions {
  store: Store<GlobalState>;
  abrechnungService: AbrechnungService;
  kassenbuchService: KassenbuchService;
  studioService: StudioService;
  termineService: TermineService;
}

class ZnaApiImpl implements ZnaApi {
  private store: Store<GlobalState>;
  private abrechnungService: AbrechnungService;
  private kassenbuchService: KassenbuchService;
  private studioService: StudioService;
  private termineService: TermineService;
  constructor(private Vue: typeof _Vue, options: ZnaApiOptions) {
    this.store = options.store;
    this.abrechnungService = options.abrechnungService;
    this.kassenbuchService = options.kassenbuchService;
    this.studioService = options.studioService;
    this.termineService = options.termineService;
  }

  public getTitle(): string {
    return document.title;
  }
  public setTitle(title: string): void {
    document.title = title;
  }

  public registerAuthErrorObserver(
    next?: ((value: ZnaRequestError) => void) | undefined,
    error?: ((error: ZnaRequestError) => void) | undefined,
    complete?: (() => void) | undefined
  ): Subscription {
    return registerAuthErrorObserver(next, error, complete);
  }

  public registerSuccessObserver(
    next?: ((value: any) => void) | undefined,
    error?: ((error: ZnaRequestError) => void) | undefined,
    complete?: (() => void) | undefined
  ): Subscription {
    return registerSuccessObserver(next, error, complete);
  }

  public isAuthenticated(): boolean {
    return this.store.getters["auth/isAuthenticated"];
  }

  public isStudio(): boolean {
    return this.store.getters["auth/isStudio"];
  }

  public darfTerminplanVonStudio(studioBezeichner: string): boolean {
    return this.store.getters["auth/darfTerminplanVonStudio"](studioBezeichner);
  }

  public darfDienstplanVonStudioLesen(studioBezeichner: string): boolean {
    return this.store.getters["auth/darfDienstplanVonStudioLesen"](studioBezeichner);
  }

  public darfDienstplanVonStudioSchreiben(studioBezeichner: string): boolean {
    return this.store.getters["auth/darfDienstplanVonStudioSchreiben"](studioBezeichner);
  }

  public darfBuchhaltung(): boolean {
    return this.store.getters["auth/darfBuchhaltung"];
  }

  public darfBuchhaltungTagesabschluss(): boolean {
    return this.store.getters["auth/darfBuchhaltungTagesabschluss"];
  }

  public darfNotAus(): boolean {
    return this.store.getters["auth/darfNotAus"];
  }

  public darfTageAuswaehlen(): boolean {
    return this.store.getters["auth/darfTageAuswaehlen"];
  }

  public darfGeloeschteTermine(): boolean {
    return this.store.getters["auth/darfGeloeschteTermine"];
  }

  public darfTageLoeschen(): boolean {
    return this.store.getters["auth/darfTageLoeschen"];
  }

  public isAppClientConfigured(): boolean {
    return isAppClientConfigured();
  }

  public listResourceScopes(): Observable<any[]> {
    return listResourceScopes();
  }

  public createAppClient(name: string, scope: string[]): Observable<any> {
    return createAppClient(name, scope);
  }

  public createAdminUser(username: string, otpSecret: string): Observable<any> {
    return createAdminUser(username, otpSecret);
  }

  public createUser(username: string): Observable<any> {
    return createUser(username);
  }

  public needs2FA(username: string): Observable<boolean> {
    return needs2FA(username);
  }

  public logout(forceLogin: boolean) {
    return logout(forceLogin);
  }

  public exchangeAuthCode(code: string): Observable<any> {
    return exchangeAuthCode(code);
  }

  public cleanupLocalStorage() {
    cleanupLocalStorage();
  }

  public increment(sequenzName: string): Observable<number> {
    return incrementAndGet(sequenzName);
  }

  public getStudios(): Observable<Studio[]> {
    return this.studioService.getStudios();
  }

  public getStudio(standortBezeichner: string): Observable<Studio> {
    return this.studioService.getStudio(standortBezeichner);
  }

  public studioSpeichern(standort: Studio): Observable<Studio> {
    return this.studioService.studioSpeichern(standort);
  }

  public getVerfuegbareDamen(studio: Studio): Observable<Dame[]> {
    return this.termineService.getVerfuegbareDamen(studio);
  }
  public getDienstplanKw(
    studio: Studio,
    tag: Moment
  ): Observable<DienstplanEintrag[]> {
    return this.termineService.getDienstplanKw(studio, tag);
  }
  public getDienstplanTag(
    studio: Studio,
    tag: Moment,
    gesamtenTagAnzeigen: boolean
  ): Observable<DienstplanEintrag[]> {
    return this.termineService.getDienstplanTag(
      studio,
      tag,
      gesamtenTagAnzeigen
    );
  }
  public dienstSpeichern(
    dienst: DienstplanEintrag
  ): Observable<DienstplanEintrag> {
    return this.termineService.dienstSpeichern(dienst);
  }
  public dienstLoeschen(dienst: DienstplanEintrag): Observable<any> {
    return this.termineService.dienstLoeschen(dienst);
  }

  public getTermine(
    studio: Studio,
    datum: Moment,
    gesamtenTagAnzeigen: boolean
  ): Observable<TerminReihe[]> {
    return this.termineService.getTermine(studio, datum, gesamtenTagAnzeigen);
  }
  public getTermineMonat(
    studio: Studio,
    monat: Moment,
    alleDamenZurueckgeben: boolean,
    dame?: Dame
  ): Observable<MonatsabschlussZeile[]> {
    return this.termineService.getTermineMonat(studio, monat, alleDamenZurueckgeben, dame);
  }
  public buchungstagVerschieben(
    studio: Studio,
    monat: Moment,
    dame: Dame,
    vonTag: string,
    nachTag: string,
    alleDamenZurueckgeben: boolean
  ): Observable<MonatsabschlussZeile[] | string> {
    return this.termineService.buchungstagVerschieben(
      studio,
      monat,
      dame,
      vonTag,
      nachTag,
      alleDamenZurueckgeben
    );
  }
  public buchungstagSpeichern(
    studio: Studio,
    monat: Moment,
    dame: Dame,
    tag: CalendarDay
  ): Observable<void> {
    return this.termineService.buchungstagSpeichern(studio, monat, dame, tag);
  }
  public terminSpeichern(studio: Studio, termin: Termin): Observable<Termin> {
    return this.termineService.terminSpeichern(studio, termin);
  }
  public termineAufHeute(studio: Studio): Observable<Termin[]> {
    return this.termineService.termineAufHeute(studio);
  }
  public tagesTermineLoeschen(studio: Studio, tag: Moment): Observable<void> {
    return this.termineService.tagesTermineLoeschen(studio, tag);
  }
  public alleBuchungsdatenLoeschen(): Observable<void> {
    return this.termineService.alleBuchungsdatenLoeschen();
  }
  public monatsabschlussNeuBerechnen(studio: Studio, monat: Moment, alleDamenZurueckgeben: boolean, dame?: Dame): Observable<MonatsabschlussZeile[]> {
    return this.abrechnungService.monatsabschlussNeuBerechnen(studio, monat, alleDamenZurueckgeben, dame);
  }
  public updateMonatsabschlussDame(studio: Studio, tag: Moment, dame: number, miete: number): Observable<string[]> {
    return this.abrechnungService.updateMonatsabschlussDame(studio, tag, dame, miete);
  }
  public getKassenbuch(monat: Moment): Observable<Kassenbuch> {
    return this.kassenbuchService.getKassenbuch(monat);
  }
  public getKassenbuchEinnahmen(monat: Moment): Observable<MonatsabschlussZeile[]> {
    return this.kassenbuchService.getKassenbuchEinnahmen(monat);
  }
  public getKassenbuchAusgaben(monat: Moment): Observable<Ausgabe[]> {
    return this.kassenbuchService.getKassenbuchAusgaben(monat);
  }
  public updateKassenbuch(monat: Moment, kassenbuch: Kassenbuch): Observable<string[]> {
    return this.kassenbuchService.updateKassenbuch(monat, kassenbuch);
  }
  public updateSignature(pngData: string): Observable<void> {
    const url = `/unterschrift`;
    const scopeConfig = {
      requiredScope: "openid profile",
      requiredRoles: [], // Für Jens Prüfer
    } as any;
    return _unwrapAxiosResponse<void>(httpClient.post(url, pngData, scopeConfig));
  }
  public monatseinnahmenVerschieben(monat: Moment, sourceStudio: Studio, targetStudio: Studio, betrag: number): Observable<string[]> {
    return this.abrechnungService.monatseinnahmenVerschieben(monat, sourceStudio, targetStudio, betrag);
  }
}

export default {
  install(Vue: typeof _Vue, options: ZnaApiOptions) {
    if (!Vue.prototype.$znaApi) {
      Vue.prototype.$znaApi = new ZnaApiImpl(Vue, options);
    }
  },
};

declare module "vue/types/vue" {
  interface Vue {
    $znaApi: ZnaApi;
  }
}
